import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ChevronLeftIcon, ChevronRightIcon, MinusIcon, PlusIcon, CheckIcon, CalendarIcon } from 'lucide-react';
interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
}
const ROOM_TYPES = [{
  id: 'single',
  name: 'Einzelzimmer',
  price: 49.95,
  maxGuests: 1
}, {
  id: 'double',
  name: 'Doppelzimmer',
  price: 69.95,
  maxGuests: 2
}, {
  id: 'quad',
  name: '4-Bett Zimmer',
  price: 99.95,
  maxGuests: 4
}];
const DAY_NAMES = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const MONTH_NAMES = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
function isSameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function formatDate(d: Date) {
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
}
// Generate some fake unavailable dates
function getUnavailableDates(year: number, month: number): Set<number> {
  const seed = year * 100 + month;
  const days = new Set<number>();
  // Pseudo-random based on month
  const candidates = [5, 6, 12, 13, 19, 20, 26, 27];
  candidates.forEach(d => {
    if ((seed + d) % 3 === 0) days.add(d);
  });
  return days;
}
function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}
function getFirstDayOfWeek(year: number, month: number) {
  const day = new Date(year, month, 1).getDay();
  return day === 0 ? 6 : day - 1; // Monday = 0
}
export function BookingModal({
  isOpen,
  onClose
}: BookingModalProps) {
  const today = useMemo(() => new Date(), []);
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [checkIn, setCheckIn] = useState<Date | null>(null);
  const [checkOut, setCheckOut] = useState<Date | null>(null);
  const [roomType, setRoomType] = useState(ROOM_TYPES[0]);
  const [guests, setGuests] = useState(1);
  const [breakfast, setBreakfast] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  useEffect(() => {
    if (!isOpen) {
      setSubmitted(false);
    }
  }, [isOpen]);
  useEffect(() => {
    if (guests > roomType.maxGuests) setGuests(roomType.maxGuests);
  }, [roomType, guests]);
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  const unavailable = useMemo(() => getUnavailableDates(viewYear, viewMonth), [viewYear, viewMonth]);
  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfWeek(viewYear, viewMonth);
  const handleDayClick = useCallback((day: number) => {
    const date = new Date(viewYear, viewMonth, day);
    if (!checkIn || checkIn && checkOut) {
      setCheckIn(date);
      setCheckOut(null);
    } else {
      if (date <= checkIn) {
        setCheckIn(date);
        setCheckOut(null);
      } else {
        setCheckOut(date);
      }
    }
  }, [viewYear, viewMonth, checkIn, checkOut]);
  const prevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(viewYear - 1);
    } else setViewMonth(viewMonth - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(viewYear + 1);
    } else setViewMonth(viewMonth + 1);
  };
  const nights = checkIn && checkOut ? Math.round((checkOut.getTime() - checkIn.getTime()) / 86400000) : 0;
  const roomTotal = nights * roomType.price;
  const breakfastTotal = breakfast ? nights * guests * 9.95 : 0;
  const total = roomTotal + breakfastTotal;
  const canSubmit = checkIn && checkOut && nights > 0;
  const handleSubmit = () => {
    if (!canSubmit) return;
    setSubmitted(true);
    setTimeout(() => onClose(), 2000);
  };
  const isPast = (day: number) => {
    const d = new Date(viewYear, viewMonth, day);
    const t = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return d < t;
  };
  const isInRange = (day: number) => {
    if (!checkIn || !checkOut) return false;
    const d = new Date(viewYear, viewMonth, day);
    return d > checkIn && d < checkOut;
  };
  const isCheckIn = (day: number) => checkIn && isSameDay(new Date(viewYear, viewMonth, day), checkIn);
  const isCheckOut = (day: number) => checkOut && isSameDay(new Date(viewYear, viewMonth, day), checkOut);
  const isToday = (day: number) => isSameDay(new Date(viewYear, viewMonth, day), today);
  return <AnimatePresence data-id="element-19">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={onClose} data-id="element-20">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" data-id="element-21" />

          {/* Modal */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        type: 'spring',
        stiffness: 400,
        damping: 30
      }} onClick={e => e.stopPropagation()} className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl bg-[#0d0d0d] border border-white/10 shadow-2xl" data-id="element-22">
            {/* Success State */}
            <AnimatePresence data-id="element-23">
              {submitted && <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-[#0d0d0d] rounded-3xl" data-id="element-24">
                  <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              type: 'spring',
              stiffness: 300,
              damping: 20,
              delay: 0.1
            }} className="w-20 h-20 rounded-full bg-electric-blue/20 flex items-center justify-center mb-6" data-id="element-25">
                    <CheckIcon className="w-10 h-10 text-electric-blue" strokeWidth={2.5} data-id="element-26" />
                  </motion.div>
                  <motion.p initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.3
            }} className="text-2xl font-heading font-bold text-white mb-2" data-id="element-27">
                    Anfrage gesendet!
                  </motion.p>
                  <motion.p initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.5
            }} className="text-white/60" data-id="element-28">
                    Wir melden uns in Kürze bei Ihnen.
                  </motion.p>
                </motion.div>}
            </AnimatePresence>

            <div className="p-6 md:p-8" data-id="element-29">
              {/* Header */}
              <div className="flex items-center justify-between mb-8" data-id="element-30">
                <h2 className="text-2xl font-heading font-bold text-white" data-id="element-31">
                  Zimmer buchen
                </h2>
                <button onClick={onClose} className="w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors" aria-label="Schließen" data-id="element-32">
                  <XIcon className="w-5 h-5 text-white/60" data-id="element-33" />
                </button>
              </div>

              {/* Calendar */}
              <div className="mb-8" data-id="element-34">
                <div className="flex items-center gap-2 mb-4" data-id="element-35">
                  <CalendarIcon className="w-5 h-5 text-electric-blue" data-id="element-36" />
                  <h3 className="text-lg font-heading font-bold text-white" data-id="element-37">
                    Reisezeitraum
                  </h3>
                </div>

                <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-4 md:p-6" data-id="element-38">
                  {/* Month Nav */}
                  <div className="flex items-center justify-between mb-4" data-id="element-39">
                    <button onClick={prevMonth} className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors" data-id="element-40">
                      <ChevronLeftIcon className="w-4 h-4 text-white/60" data-id="element-41" />
                    </button>
                    <span className="text-white font-heading font-bold" data-id="element-42">
                      {MONTH_NAMES[viewMonth]} {viewYear}
                    </span>
                    <button onClick={nextMonth} className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors" data-id="element-43">
                      <ChevronRightIcon className="w-4 h-4 text-white/60" data-id="element-44" />
                    </button>
                  </div>

                  {/* Day Headers */}
                  <div className="grid grid-cols-7 gap-1 mb-2" data-id="element-45">
                    {DAY_NAMES.map(d => <div key={d} className="text-center text-xs text-white/40 font-medium py-1" data-id="element-46">
                        {d}
                      </div>)}
                  </div>

                  {/* Day Grid */}
                  <div className="grid grid-cols-7 gap-1" data-id="element-47">
                    {Array.from({
                  length: firstDay
                }).map((_, i) => <div key={`empty-${i}`} data-id="element-48" />)}
                    {Array.from({
                  length: daysInMonth
                }).map((_, i) => {
                  const day = i + 1;
                  const past = isPast(day);
                  const unav = unavailable.has(day);
                  const disabled = past || unav;
                  const inRange = isInRange(day);
                  const cIn = isCheckIn(day);
                  const cOut = isCheckOut(day);
                  const td = isToday(day);
                  return <button key={day} disabled={disabled} onClick={() => handleDayClick(day)} className={`
                            relative h-10 rounded-lg text-sm font-medium transition-all
                            ${disabled ? 'text-white/20 cursor-not-allowed' : 'hover:bg-white/10 cursor-pointer'}
                            ${unav && !past ? 'line-through text-red-400/40' : ''}
                            ${inRange ? 'bg-electric-blue/15 text-white' : ''}
                            ${cIn || cOut ? 'bg-electric-blue text-white font-bold' : ''}
                            ${td && !cIn && !cOut ? 'ring-1 ring-white/30' : ''}
                            ${!disabled && !inRange && !cIn && !cOut ? 'text-white/70' : ''}
                          `} data-id="element-49">
                          {day}
                        </button>;
                })}
                  </div>

                  {/* Selected Dates Display */}
                  <div className="mt-4 flex flex-wrap gap-4 text-sm" data-id="element-50">
                    <div data-id="element-51">
                      <span className="text-white/40" data-id="element-52">Anreise: </span>
                      <span className="text-white font-medium" data-id="element-53">
                        {checkIn ? formatDate(checkIn) : '—'}
                      </span>
                    </div>
                    <div data-id="element-54">
                      <span className="text-white/40" data-id="element-55">Abreise: </span>
                      <span className="text-white font-medium" data-id="element-56">
                        {checkOut ? formatDate(checkOut) : '—'}
                      </span>
                    </div>
                    {nights > 0 && <div className="text-electric-blue font-medium" data-id="element-57">
                        {nights} {nights === 1 ? 'Nacht' : 'Nächte'}
                      </div>}
                  </div>
                </div>
              </div>

              {/* Room Selection */}
              <div className="mb-8" data-id="element-58">
                <h3 className="text-lg font-heading font-bold text-white mb-4" data-id="element-59">
                  Zimmertyp
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-60">
                  {ROOM_TYPES.map(room => <button key={room.id} onClick={() => setRoomType(room)} className={`p-4 rounded-xl border text-left transition-all ${roomType.id === room.id ? 'border-electric-blue bg-electric-blue/10 shadow-[0_0_20px_rgba(14,165,233,0.15)]' : 'border-white/10 bg-white/[0.02] hover:border-white/20'}`} data-id="element-61">
                      <p className="font-bold text-white text-sm mb-1" data-id="element-62">
                        {room.name}
                      </p>
                      <p className="text-electric-blue font-heading font-bold text-lg" data-id="element-63">
                        {room.price.toFixed(2).replace('.', ',')} €
                      </p>
                      <p className="text-white/40 text-xs" data-id="element-64">pro Nacht</p>
                    </button>)}
                </div>
              </div>

              {/* Guests & Breakfast */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8" data-id="element-65">
                {/* Guests */}
                <div data-id="element-66">
                  <h3 className="text-lg font-heading font-bold text-white mb-4" data-id="element-67">
                    Personen
                  </h3>
                  <div className="flex items-center gap-4" data-id="element-68">
                    <button onClick={() => setGuests(Math.max(1, guests - 1))} disabled={guests <= 1} className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-69">
                      <MinusIcon className="w-4 h-4 text-white" data-id="element-70" />
                    </button>
                    <span className="text-2xl font-heading font-bold text-white w-8 text-center" data-id="element-71">
                      {guests}
                    </span>
                    <button onClick={() => setGuests(Math.min(roomType.maxGuests, guests + 1))} disabled={guests >= roomType.maxGuests} className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-72">
                      <PlusIcon className="w-4 h-4 text-white" data-id="element-73" />
                    </button>
                    <span className="text-white/40 text-sm" data-id="element-74">
                      max. {roomType.maxGuests}
                    </span>
                  </div>
                </div>

                {/* Breakfast */}
                <div data-id="element-75">
                  <h3 className="text-lg font-heading font-bold text-white mb-4" data-id="element-76">
                    Frühstück
                  </h3>
                  <button onClick={() => setBreakfast(!breakfast)} className={`w-full p-4 rounded-xl border text-left transition-all flex items-center justify-between ${breakfast ? 'border-electric-blue bg-electric-blue/10' : 'border-white/10 bg-white/[0.02] hover:border-white/20'}`} data-id="element-77">
                    <div data-id="element-78">
                      <p className="font-medium text-white text-sm" data-id="element-79">
                        Frühstück hinzufügen
                      </p>
                      <p className="text-white/40 text-xs" data-id="element-80">
                        9,95 € pro Person / Nacht
                      </p>
                    </div>
                    <div className={`w-12 h-7 rounded-full p-1 transition-colors ${breakfast ? 'bg-electric-blue' : 'bg-white/10'}`} data-id="element-81">
                      <motion.div animate={{
                    x: breakfast ? 20 : 0
                  }} transition={{
                    type: 'spring',
                    stiffness: 500,
                    damping: 30
                  }} className="w-5 h-5 rounded-full bg-white shadow-sm" data-id="element-82" />
                    </div>
                  </button>
                </div>
              </div>

              {/* Price Summary */}
              {canSubmit && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} className="mb-8 bg-white/[0.03] border border-white/10 rounded-2xl p-5" data-id="element-83">
                  <h3 className="text-sm font-bold text-white/60 uppercase tracking-wider mb-3" data-id="element-84">
                    Zusammenfassung
                  </h3>
                  <div className="space-y-2 text-sm" data-id="element-85">
                    <div className="flex justify-between text-white/70" data-id="element-86">
                      <span data-id="element-87">
                        {nights} × {roomType.name}
                      </span>
                      <span data-id="element-88">{roomTotal.toFixed(2).replace('.', ',')} €</span>
                    </div>
                    {breakfast && <div className="flex justify-between text-white/70" data-id="element-89">
                        <span data-id="element-90">
                          {nights} × {guests} × Frühstück
                        </span>
                        <span data-id="element-91">
                          {breakfastTotal.toFixed(2).replace('.', ',')} €
                        </span>
                      </div>}
                    <div className="h-px bg-white/10 my-2" data-id="element-92" />
                    <div className="flex justify-between items-baseline" data-id="element-93">
                      <span className="text-white font-bold" data-id="element-94">Gesamt</span>
                      <span className="text-2xl font-heading font-bold text-electric-blue" data-id="element-95">
                        {total.toFixed(2).replace('.', ',')} €
                      </span>
                    </div>
                  </div>
                </motion.div>}

              {/* Submit */}
              <button onClick={handleSubmit} disabled={!canSubmit} className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${canSubmit ? 'bg-electric-blue text-white hover:bg-electric-blue/90 shadow-[0_0_30px_rgba(14,165,233,0.3)]' : 'bg-white/5 text-white/30 cursor-not-allowed'}`} data-id="element-96">
                Buchungsanfrage senden
              </button>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}